import type {
  LangChainMessage,
  LangGraphMessagesEvent
} from "@assistant-ui/react-langgraph";

/**
 * Keeps context-compaction machinery out of the chat (MYT-1781, PE-626).
 *
 * deepagents' SummarizationMiddleware compacts a long thread by asking the
 * model to summarise older turns (the SESSION INTENT / SUMMARY / ... block).
 * It leaves `messages` intact and records the result in a `_summarization_event`
 * state field: `{ cutoff_index, summary_message }`, where the model from then
 * on sees the summary followed by `messages[cutoff_index:]`. None of that is
 * something the user asked to read, so:
 *
 * - The backend silences the summary call and announces it instead as a
 *   `custom` event `{ type: "context_compaction", status }`. That event drives
 *   the "Automatically compacting context" indicator and is swallowed here.
 * - Should a summary call ever stream again, its tokens are dropped as a
 *   second line of defence, in either shape a server uses: the SDK tuple
 *   `[chunk, metadata]`, or Aegra's split of one `messages/metadata` event
 *   (the only one carrying the tag) followed by `messages/partial` events for
 *   the same message id. The split is how the PE-626 leak got past the
 *   previous version of this filter.
 * - A summary message that does reach the message list (LangChain's own
 *   middleware rewrites `messages`) is removed from it.
 * - The `_summarization_event` is surfaced to the caller as a
 *   {@link ContextCompaction}: the summary text plus the id of the first
 *   message the agent still sees, so the thread can draw an "earlier
 *   conversation compacted" divider above that message.
 */

const SUMMARIZATION_SOURCE = "summarization";
const SUMMARIZATION_EVENT_KEY = "_summarization_event";
const MESSAGE_EVENT_TYPES = new Set([
  "messages",
  "messages/partial",
  "messages/complete"
]);
const MESSAGE_METADATA_EVENT = "messages/metadata";
const CUSTOM_EVENT = "custom";

/** `type` of the backend's custom stream event announcing a compaction. */
export const CONTEXT_COMPACTION_EVENT_TYPE = "context_compaction";

/** Lead-in LangChain prepends to the summary; dropped from the shown text. */
const SUMMARY_PREFIX = "Here is a summary of the conversation to date:";

/** deepagents' `_summarization_event`, as it arrives in thread state. */
export type SummarizationEvent = {
  cutoff_index: number;
  summary_message: Record<string, unknown>;
};

/** Where and what the agent has compacted, ready for the thread to render. */
export type ContextCompaction = {
  /**
   * Id of the first message the agent still sees in full; the divider belongs
   * above it. Null when the cutoff falls past the last visible message.
   */
  anchorMessageId: string | null;
  /** The summary the agent now works from, without LangChain's lead-in. */
  summary: string;
};

type CompactingChangeHandler = (isCompacting: boolean) => void;

/**
 * Receives each `_summarization_event` seen on the stream. `values` is the
 * full thread state when the event arrived in a `values` event, and null when
 * it arrived in an `updates` delta, where the caller must fetch the state to
 * resolve the anchor (see {@link compactionFromValues}).
 */
type CompactionHandler = (
  event: SummarizationEvent,
  values: Record<string, unknown> | null
) => void;

export type SummarizationStreamOptions = {
  onCompactingChange: CompactingChangeHandler;
  onCompaction?: CompactionHandler;
};

/** Why the indicator is on: announced by the backend, or inferred from tokens. */
type CompactingSource = "announced" | "tokens";

const eventType = (event: string) => event.split("|", 1)[0];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const hasSummarizationSource = (value: unknown): boolean =>
  isRecord(value) && value.lc_source === SUMMARIZATION_SOURCE;

const isSummarizationMessage = (value: unknown): boolean => {
  if (!isRecord(value)) return false;
  return (
    hasSummarizationSource(value.additional_kwargs) ||
    hasSummarizationSource(value.response_metadata)
  );
};

const messageId = (value: unknown): string | null => {
  if (!isRecord(value)) return null;
  return typeof value.id === "string" && value.id ? value.id : null;
};

// ----------------------------------------------------------------------------
// Compaction events in thread state
// ----------------------------------------------------------------------------

const messageText = (content: unknown): string => {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((part) =>
      isRecord(part) && part.type === "text" && typeof part.text === "string"
        ? part.text
        : ""
    )
    .join("");
};

const summaryText = (content: unknown): string => {
  const text = messageText(content).trim();
  return text.startsWith(SUMMARY_PREFIX)
    ? text.slice(SUMMARY_PREFIX.length).trim()
    : text;
};

/**
 * The `_summarization_event` carried by a state object, if well-formed.
 * @param values - Thread state values, or one node's update.
 * @returns The event, or null when absent or malformed.
 */
export const summarizationEventOf = (
  values: unknown
): SummarizationEvent | null => {
  if (!isRecord(values)) return null;
  const event = values[SUMMARIZATION_EVENT_KEY];
  if (!isRecord(event)) return null;
  const { cutoff_index: cutoffIndex, summary_message: summaryMessage } = event;
  if (typeof cutoffIndex !== "number" || !isRecord(summaryMessage)) return null;
  return { cutoff_index: cutoffIndex, summary_message: summaryMessage };
};

/**
 * The `_summarization_event` inside an `updates` payload, which nests each
 * node's update under the node name, or a `values` payload.
 * @param data - The event payload.
 * @returns The event, or null when no node reported one.
 */
const summarizationEventIn = (data: unknown): SummarizationEvent | null => {
  const direct = summarizationEventOf(data);
  if (direct) return direct;
  if (!isRecord(data)) return null;
  for (const child of Object.values(data)) {
    const nested = summarizationEventOf(child);
    if (nested) return nested;
  }
  return null;
};

/**
 * The first message at or after `cutoffIndex` that the thread shows on its
 * own row. Tool messages fold into the assistant turn that called them, so a
 * cutoff landing on one anchors to the next human or assistant message.
 * @param messages - The thread's raw message list.
 * @param cutoffIndex - deepagents' cutoff into that list.
 * @returns The anchor message id, or null when none follows the cutoff.
 */
const anchorMessageId = (
  messages: readonly unknown[],
  cutoffIndex: number
): string | null => {
  for (
    let index = Math.max(cutoffIndex, 0);
    index < messages.length;
    index += 1
  ) {
    const message = messages[index];
    if (!isRecord(message)) continue;
    if (message.type !== "human" && message.type !== "ai") continue;
    const id = messageId(message);
    if (id) return id;
  }
  return null;
};

/**
 * Resolve the compaction recorded in thread state to something the thread can
 * render.
 * @param values - Full thread state values (`threads.getState`, a `values` event).
 * @returns The compaction, or null when the thread has not been compacted.
 */
export const compactionFromValues = (
  values: unknown
): ContextCompaction | null => {
  const event = summarizationEventOf(values);
  if (!event || !isRecord(values)) return null;
  const messages = Array.isArray(values.messages) ? values.messages : [];
  return {
    anchorMessageId: anchorMessageId(messages, event.cutoff_index),
    summary: summaryText(event.summary_message.content)
  };
};

// ----------------------------------------------------------------------------
// Summary messages in the message list
// ----------------------------------------------------------------------------

const removeSummarizationMessages = <T>(messages: T[]): T[] =>
  messages.filter((message) => !isSummarizationMessage(message));

/**
 * Drop summary messages from a thread-state values object. Returns the same
 * object when there is nothing to remove.
 * @param values - Thread state values.
 * @returns `values`, or a copy without summary messages.
 */
export const sanitizeSummarizationValues = <
  T extends { messages?: LangChainMessage[] }
>(
  values: T
): T => {
  const { messages } = values;
  if (!Array.isArray(messages)) return values;

  const filtered = removeSummarizationMessages(messages);
  return filtered.length === messages.length
    ? values
    : { ...values, messages: filtered };
};

const sanitizeNestedMessages = (value: unknown): unknown => {
  if (!isRecord(value)) return value;

  let changed = false;
  const next: Record<string, unknown> = { ...value };

  if (Array.isArray(value.messages)) {
    const filtered = removeSummarizationMessages(value.messages);
    if (filtered.length !== value.messages.length) {
      next.messages = filtered;
      changed = true;
    }
  }

  for (const [key, child] of Object.entries(value)) {
    if (!isRecord(child) || !Array.isArray(child.messages)) continue;
    const filtered = removeSummarizationMessages(child.messages);
    if (filtered.length === child.messages.length) continue;
    next[key] = { ...child, messages: filtered };
    changed = true;
  }

  return changed ? next : value;
};

// ----------------------------------------------------------------------------
// Stream events
// ----------------------------------------------------------------------------

const compactionStatus = (
  event: LangGraphMessagesEvent<LangChainMessage>
): "started" | "finished" | null => {
  if (eventType(event.event) !== CUSTOM_EVENT) return null;
  const data: unknown = event.data;
  if (!isRecord(data) || data.type !== CONTEXT_COMPACTION_EVENT_TYPE) {
    return null;
  }
  return data.status === "started" ? "started" : "finished";
};

/**
 * SDK tuple shape: `[chunk, metadata]` with the tag on the metadata.
 * @param event - A stream event.
 * @returns Whether the event carries summary tokens.
 */
const isSummarizationTupleEvent = (
  event: LangGraphMessagesEvent<LangChainMessage>
): boolean => {
  if (!MESSAGE_EVENT_TYPES.has(eventType(event.event))) return false;
  if (!Array.isArray(event.data)) return false;

  const [message, metadata] = event.data;
  return hasSummarizationSource(metadata) || isSummarizationMessage(message);
};

/**
 * Aegra shape: `messages/metadata` is `{ [messageId]: { metadata } }`, sent
 * once per message before its first `messages/partial`.
 * @param data - The `messages/metadata` event payload.
 * @returns The message ids whose metadata carries the summarization tag.
 */
const summaryIdsInMetadataEvent = (data: unknown): string[] => {
  if (!isRecord(data)) return [];
  return Object.entries(data)
    .filter(
      ([, entry]) => isRecord(entry) && hasSummarizationSource(entry.metadata)
    )
    .map(([id]) => id);
};

const withoutKeys = (
  data: Record<string, unknown>,
  keys: readonly string[]
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(data).filter(([key]) => !keys.includes(key))
  );

/**
 * Drop summary messages from a `messages/partial` / `messages/complete` list,
 * by tag or by an id learned from an earlier `messages/metadata` event.
 * @param event - The message-list event.
 * @param summaryMessageIds - Ids announced as summaries by metadata events.
 * @returns The event without summary messages, and whether that emptied it.
 */
const dropSummaryMessages = (
  event: LangGraphMessagesEvent<LangChainMessage>,
  summaryMessageIds: ReadonlySet<string>
): {
  event: LangGraphMessagesEvent<LangChainMessage>;
  droppedAll: boolean;
} => {
  if (!Array.isArray(event.data)) return { event, droppedAll: false };
  if (event.data.length === 2 && hasSummarizationSource(event.data[1])) {
    return { event, droppedAll: false };
  }

  const kept = event.data.filter((message) => {
    const id = messageId(message);
    return (
      !(id && summaryMessageIds.has(id)) && !isSummarizationMessage(message)
    );
  });
  if (kept.length === event.data.length) return { event, droppedAll: false };
  return {
    event: { ...event, data: kept },
    droppedAll: kept.length === 0 && event.data.length > 0
  };
};

/**
 * Filter a run's event stream: swallow compaction announcements and any
 * streamed summary tokens, drive the compacting indicator, report compaction
 * events found in state, and drop summary messages from message lists. Every
 * other event is yielded unchanged.
 * @param generator - The upstream LangGraph event stream.
 * @param options - `onCompactingChange` receives `true` while the agent is
 *   compacting; `onCompaction` receives each `_summarization_event` seen.
 * @yields The filtered stream.
 */
export async function* filterSummarizationStream(
  generator: AsyncGenerator<LangGraphMessagesEvent<LangChainMessage>>,
  options: SummarizationStreamOptions
): AsyncGenerator<LangGraphMessagesEvent<LangChainMessage>> {
  const { onCompactingChange, onCompaction } = options;
  let compacting: CompactingSource | null = null;
  const setCompacting = (next: CompactingSource | null) => {
    const was = compacting !== null;
    compacting = next;
    if (was !== (next !== null)) onCompactingChange(next !== null);
  };
  const summaryMessageIds = new Set<string>();

  try {
    for await (const event of generator) {
      const status = compactionStatus(event);
      if (status) {
        setCompacting(status === "started" ? "announced" : null);
        continue;
      }

      const type = eventType(event.event);
      if (type === MESSAGE_METADATA_EVENT) {
        const summaryIds = summaryIdsInMetadataEvent(event.data);
        if (summaryIds.length) {
          summaryIds.forEach((id) => summaryMessageIds.add(id));
          setCompacting("tokens");
          const rest = withoutKeys(
            event.data as Record<string, unknown>,
            summaryIds
          );
          if (!Object.keys(rest).length) continue;
          yield {
            ...event,
            data: rest
          } as LangGraphMessagesEvent<LangChainMessage>;
          continue;
        }
      }

      if (isSummarizationTupleEvent(event)) {
        setCompacting("tokens");
        continue;
      }

      if (type === "messages/partial" || type === "messages/complete") {
        const dropped = dropSummaryMessages(event, summaryMessageIds);
        if (dropped.droppedAll) {
          setCompacting("tokens");
          continue;
        }
        if (compacting === "tokens") setCompacting(null);
        yield dropped.event;
        continue;
      }

      if (compacting === "tokens") setCompacting(null);

      if (type === "updates" || type === "values") {
        const compaction = summarizationEventIn(event.data);
        if (compaction && onCompaction) {
          onCompaction(
            compaction,
            type === "values" && isRecord(event.data) ? event.data : null
          );
        }
        const data = sanitizeNestedMessages(event.data);
        yield data === event.data
          ? event
          : ({ ...event, data } as LangGraphMessagesEvent<LangChainMessage>);
        continue;
      }

      yield event;
    }
  } finally {
    setCompacting(null);
  }
}
